import React from 'react';
import { Database, Cpu, TrendingUp, Zap, Settings, BarChart3, Shield, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const SistemasERP = () => {
  const features = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: 'Inteligência Artificial',
      description: 'Automação inteligente com IA para otimizar processos'
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: 'Gestão Integrada',
      description: 'Todos os setores da empresa conectados em um só lugar'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Relatórios Avançados',
      description: 'Dashboards e análises em tempo real para decisões estratégicas'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Alta Performance',
      description: 'Sistema rápido e responsivo para máxima produtividade'
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: '100% Personalizado',
      description: 'Desenvolvido sob medida para seu negócio'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Segurança Total',
      description: 'Proteção de dados com criptografia avançada'
    }
  ];

  const benefits = [
    'Redução de custos operacionais',
    'Aumento da produtividade empresarial',
    'Eliminação de retrabalho com automação',
    'Tomada de decisão baseada em dados',
    'Integração com APIs e sistemas externos',
    'Suporte técnico empresarial especializado'
  ];

  return (
    <section id="sistemas-erp" className="py-20 bg-gradient-to-br from-violet-950 via-purple-900 to-indigo-950 relative overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-violet-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500 rounded-full filter blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-5 overflow-hidden pointer-events-none">
        <div className="w-full h-full" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-violet-500/20 border border-violet-400/30 rounded-full">
            <Cpu className="w-5 h-5 text-violet-400" />
            <span className="text-violet-300 font-semibold text-sm uppercase tracking-wider">Gestão de TI para Empresas</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Sistemas <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">ERP Personalizados</span>
          </h2>
          <p className="text-xl text-violet-200 max-w-3xl mx-auto">
            Empresa de TI especializada em desenvolvimento de sistemas de gestão empresarial sob medida, potencializados por Inteligência Artificial. Consultoria de TI para automatizar e otimizar seu negócio.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group bg-white/5 backdrop-blur-sm border border-violet-500/20 rounded-2xl p-6 hover:bg-white/10 hover:border-violet-400/50 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-violet-500/30">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-violet-200">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="bg-white/5 backdrop-blur-sm border border-violet-500/20 rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="w-8 h-8 text-violet-400" />
                <h3 className="text-2xl md:text-3xl font-bold text-white">Por que escolher nosso ERP?</h3>
              </div>
              <p className="text-violet-200 mb-6">
                Nossos sistemas são desenvolvidos com as mais modernas tecnologias e recursos de IA para transformar a gestão da sua empresa.
              </p>
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-5 h-5 text-violet-400 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center">
              <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-8 shadow-2xl shadow-violet-500/30">
                <Clock className="w-12 h-12 text-white mx-auto mb-4" />
                <h4 className="text-2xl font-bold text-white mb-2">Solicite um Orçamento</h4>
                <p className="text-violet-200 mb-6">Conte-nos sobre seu projeto e receba uma proposta personalizada</p>
                <Button
                  onClick={() => {
                    const element = document.querySelector('#contato');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  size="lg"
                  className="bg-white text-violet-700 hover:bg-violet-100 font-bold px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all duration-300"
                >
                  Falar com Especialista
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SistemasERP;
